import { useEffect, useRef, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { SignatureContent } from '../../../../../../common/models/signature-tool/signature-tool.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import gmailSettingsImage from '../../../../../assets/signature-gmail-settings.png'
import { Button } from '../../../../../components/button/button.js'
import { Checkbox } from '../../../../../components/forms/checkbox/checkbox.js'
import { Check, Copy, Moon, Sun } from '../../../../../components/icon/solar-icons.js'
import { Section } from '../../../../../components/section/section.js'
import { SignatureNope } from '../../../../../components/signature/signature-nope.js'
import { Signature } from '../../../../../components/signature/signature.js'
import { copyToClipboard } from '../../../../../utils/clipboard-utils.js'
import {
    createPlainTextSignature,
    prepareSignatureElement,
} from '../../../../../utils/signature-format-utils.js'
import { portalGet } from '../../../utils/api.js'
import type { EmployeeProfileData } from '../employee-profile-types.js'
import {
    employeeToNopeSignatureContent,
    employeeToSignatureBase,
    visibleBrandVariants,
} from './signature-tab-utils.js'
import './signature-tab.css'

interface SignatureTabProps {
    employee: EmployeeProfileData
    lang: Language
}

/**
 * Self-only "Signature" tab: auto-generates the person's email signature from
 * their profile data and offers one copy button per brand the person belongs
 * to (NOPE / gotoAndPlay — both for dual-org people), each with the Alliance
 * logo stacked beneath the company logo. No creation or editing UI — identity
 * is read from the profile.
 */
export const SignatureTab = ({ employee, lang }: SignatureTabProps) => {
    const hasWorkPhone = Boolean(employee.employment?.work_phone?.trim())
    const [includePhone, setIncludePhone] = useState(hasWorkPhone)
    const [copiedId, setCopiedId] = useState<string | null>(null)
    const [failedId, setFailedId] = useState<string | null>(null)
    const [circleAvatarUrl, setCircleAvatarUrl] = useState<string | null>(null)
    const [nopeAvatarUrl, setNopeAvatarUrl] = useState<string | null>(null)
    const [darkPreviewIds, setDarkPreviewIds] = useState<Record<string, boolean>>({})
    const refs = useRef<Record<string, HTMLTableElement | null>>({})

    // The email signature photo must be a pre-rendered PNG on a public absolute
    // URL (mail clients ignore border-radius and can't reach our cookies). The
    // server crops + caches both variants — plain circle for gotoAndPlay, circle
    // + doodle composite for NOPE; null ⇒ no usable photo ⇒ render no image.
    useEffect(() => {
        let active = true
        void portalGet<{ url: string | null; nopeUrl: string | null }>(
            '/api/signature-avatar',
        ).then((res) => {
            if (active && res.ok) {
                setCircleAvatarUrl(res.data.url)
                setNopeAvatarUrl(res.data.nopeUrl)
            }
        })
        return () => {
            active = false
        }
    }, [])

    const base = employeeToSignatureBase(employee, { includePhone, avatarUrl: circleAvatarUrl })

    // People in a single organisation see one card, so there is no brand to
    // "pick" — drop the brand-choosing copy from the intro and the guide.
    const variants = visibleBrandVariants(employee.companies)
    const showBrandPicker = variants.length > 1

    const handleCopy = async (id: string, props: SignatureContent) => {
        const element = refs.current[id]
        if (!element) return

        const success = await copyToClipboard(
            prepareSignatureElement(element),
            createPlainTextSignature(props),
        ).catch(() => false)

        if (success) {
            setFailedId(null)
            setCopiedId(id)
            setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000)
        } else {
            setCopiedId(null)
            setFailedId(id)
        }
    }

    return (
        <Section className="portal-signature">
            <div className="portal-signature__intro">
                <p className="portal-signature__intro-text">
                    {showBrandPicker
                        ? t.profile.signatureIntro(lang)
                        : t.profile.signatureIntroSingle(lang)}
                </p>
            </div>

            {hasWorkPhone && (
                <Checkbox
                    className="portal-signature__toggle"
                    size="small"
                    label={t.profile.signatureIncludePhone(lang)}
                    isChecked={includePhone}
                    onChange={(isChecked) => setIncludePhone(isChecked)}
                />
            )}

            <div className="portal-signature__cards">
                {variants.map((variant) => {
                    // NOPE has its own 2026 design (layout + assets), rendered by
                    // SignatureNope from its own content; play keeps the shared renderer.
                    const isNopeVariant = variant.id === 'nope'
                    const props: SignatureContent = isNopeVariant
                        ? employeeToNopeSignatureContent(employee, {
                              includePhone,
                              avatarUrl: nopeAvatarUrl,
                          })
                        : { ...base, ...variant.brand }
                    const SignatureComponent = isNopeVariant ? SignatureNope : Signature
                    const isCopied = copiedId === variant.id
                    const isFailed = failedId === variant.id
                    const isDarkPreview = Boolean(darkPreviewIds[variant.id])

                    const setCopyRef = (el: HTMLTableElement | null) => {
                        refs.current[variant.id] = el
                    }

                    return (
                        <div key={variant.id} className="portal-signature__card">
                            <h3 className="portal-signature__card-title">{variant.label}</h3>
                            <div
                                className={
                                    isDarkPreview
                                        ? 'portal-signature__preview portal-signature__preview--dark'
                                        : 'portal-signature__preview'
                                }
                                // E-posti eelvaade ei järgi Hubi teemat (vt theme.scss).
                                data-theme="light"
                            >
                                <SignatureComponent
                                    {...props}
                                    isDark={isDarkPreview}
                                    signatureRef={isDarkPreview ? undefined : setCopyRef}
                                />
                                {/* The copied signature must always be the light version —
                                    mail clients apply their own dark-mode rendering — so
                                    while the dark preview is shown, a hidden light twin
                                    carries the copy ref. */}
                                {isDarkPreview && (
                                    <div className="portal-signature__copy-source" aria-hidden>
                                        <SignatureComponent {...props} signatureRef={setCopyRef} />
                                    </div>
                                )}
                            </div>
                            <div className="portal-signature__actions">
                                <Button
                                    size="small"
                                    variant={isCopied ? 'success' : isFailed ? 'alert' : 'primary'}
                                    iconLeft={isCopied ? Check : Copy}
                                    text={
                                        isCopied
                                            ? t.profile.signatureCopied(lang)
                                            : isFailed
                                              ? t.profile.signatureCopyFailed(lang)
                                              : t.profile.signatureCopy(lang)
                                    }
                                    onClick={() => handleCopy(variant.id, props)}
                                />
                                <Button
                                    size="small"
                                    variant="secondary"
                                    iconLeft={isDarkPreview ? Sun : Moon}
                                    text={
                                        isDarkPreview
                                            ? t.profile.signatureLightPreview(lang)
                                            : t.profile.signatureDarkPreview(lang)
                                    }
                                    onClick={() =>
                                        setDarkPreviewIds((prev) => ({
                                            ...prev,
                                            [variant.id]: !isDarkPreview,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                    )
                })}
            </div>

            <section className="portal-signature__guide">
                <h3 className="portal-signature__guide-title">
                    {t.profile.signatureGuideTitle(lang)}
                </h3>
                <ol className="portal-signature__guide-steps">
                    <li>
                        {showBrandPicker
                            ? t.profile.signatureGuideStep1(lang)
                            : t.profile.signatureGuideStep1Single(lang)}
                    </li>
                    <li>{t.profile.signatureGuideStep2(lang)}</li>
                    <li>{t.profile.signatureGuideStep3(lang)}</li>
                    <li>{t.profile.signatureGuideStep4(lang)}</li>
                </ol>
                <img
                    className="portal-signature__guide-image"
                    src={gmailSettingsImage}
                    alt={t.profile.signatureGuideImageAlt(lang)}
                />
                <p className="portal-signature__guide-help">{t.profile.signatureGuideHelp(lang)}</p>
            </section>
        </Section>
    )
}
